import type { User, CreateUserPayload, UpdateUserPayload } from '../types/user';
import type { PaginationMeta } from '../types/startup';
import { apiRequest } from '@startus/lib';

export interface GetUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  orderBy?: 'name' | 'lastname' | 'role' | 'lastLoginAt' | 'createdAt';
  sortDir?: 'asc' | 'desc';
  referenceCode?: string;
}

export interface UsersPage {
  data: User[];
  meta: PaginationMeta;
  referenceCodeCount?: number;
}

interface ApiListResponse<T> {
  success: boolean;
  data: T[];
  meta: PaginationMeta;
}

interface ApiItemResponse<T> {
  success: boolean;
  data: T;
}

interface ApiDeleteResponse {
  success: boolean;
  data: null;
}

export const usersService = {
  /**
   * Fetches all users from the backend API.
   * @returns Promise resolving to an array of User objects
   */
  async getUsers(params: GetUsersParams = {}): Promise<UsersPage> {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.search) qs.set('search', params.search);
    if (params.orderBy) qs.set('orderBy', params.orderBy);
    if (params.sortDir) qs.set('sortDir', params.sortDir);
    if (params.referenceCode) qs.set('referenceCode', params.referenceCode);
    const query = qs.toString() ? `?${qs}` : '';
    const response = await apiRequest<ApiListResponse<User> & { referenceCodeCount?: number }>(
      `/api/v1/users${query}`
    );
    return {
      data: response.data,
      meta: response.meta,
      referenceCodeCount: response.referenceCodeCount,
    };
  },

  /**
   * Creates a new user. Note: This endpoint may not be available on the backend.
   * @param payload - User creation payload
   * @returns Promise resolving to the created User object
   * @throws Error if the endpoint is not available or request fails
   */
  async createUser(payload: CreateUserPayload): Promise<User> {
    return apiRequest<User>('/api/v1/users', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Updates an existing user.
   * @param payload - User update payload including the user ID
   * @returns Promise resolving to the updated User object or null if not found
   */
  async updateUser(payload: UpdateUserPayload): Promise<User | null> {
    try {
      const { id, isActive, ...updateData } = payload;

      // Reactivation must go through /restore — general update blocks inactive users
      if (isActive === true) {
        await apiRequest<ApiItemResponse<User>>(`/api/v1/users/${id}/restore`, { method: 'PATCH' });
      }

      // Apply field updates and/or deactivation via general endpoint
      const body = { ...updateData, ...(isActive === false ? { isActive } : {}) };
      const response = await apiRequest<ApiItemResponse<User>>(`/api/v1/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });

      return response.data;
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error;
    }
  },

  /**
   * Deletes a user.
   * @param id - User ID to delete
   * @returns Promise resolving to true if deletion was successful, false otherwise
   */
  async deleteUser(id: string): Promise<boolean> {
    try {
      const response = await apiRequest<ApiDeleteResponse>(`/api/v1/users/${id}`, {
        method: 'DELETE',
      });
      return response.success === true;
    } catch (error) {
      console.error('Failed to delete user:', error);
      throw error;
    }
  },
};
